import React, { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { TooltipProps, TooltipPlacement } from '../../../types/DesignSystem/Tooltip';
import { useAnchorUpdates } from '../useAnchorUpdates';
import { useDismissable } from '../useDismissable';
import Kbd, { keysOf } from '../Kbd/Kbd';
import { withClassName } from '../cn';

/**
 * Tooltip.
 *
 * **Why this lives here.** Until 2026-08-24 the design system had an empty
 * `Tooltip/` directory while the app's real tooltip sat in feature code —
 * `Components/Common/components/HoverTooltip`, reached through
 * `Components/GMS/Rules/Components/OverlayIcon`, which `Dropdown`, `TextField` and
 * `FieldTitle` all imported. The design system depended on a feature module, so it
 * could not be the foundation for one. This is that implementation, brought in and
 * corrected. See `okf/design-system/e2e.md` § 23.
 *
 * **What was wrong with it, beyond its address.** `HoverTooltip` listens only for
 * `mouseenter`/`mouseleave` on a non-focusable `<div>`. So:
 *
 * - A keyboard user could never see any tooltip — all ~40 of the app's info
 *   tooltips were pointer-only (WCAG 2.1.1, and 1.4.13 which requires focus to
 *   trigger the same content).
 * - The bubble carried `role="tooltip"` but nothing referenced it, so a screen
 *   reader never associated it with anything. The text was, in practice, absent.
 * - There was no way to dismiss it without moving the pointer (1.4.13 again).
 *
 * This version triggers on hover **and** focus, wires `aria-describedby` onto the
 * trigger by cloning it, closes on Escape, and survives the pointer travelling
 * from trigger to bubble — the three things 1.4.13 actually asks for
 * (dismissible, hoverable, persistent).
 *
 * § 343 (2026-09-29) — three corrections, see `okf/design-system/e2e.md` § 343:
 * Escape is answered from anywhere (the tooltip is a dismissable LAYER, so the
 * topmost surface answers it); the bubble FLIPS to the opposite side before the
 * § 309 edge correction clamps it over its own trigger; and it renders into
 * `document.body` by default, as `InfoTooltip` always has.
 */

const EDGE_MARGIN = 8;

/**
 * The pointer needs time to cross the gap between trigger and bubble. Without this
 * the bubble is unreachable, which is the "hoverable" half of WCAG 1.4.13 —
 * relevant to anyone who needs to magnify the text to read it.
 */
const CLOSE_DELAY_MS = 120;

/**
 * § 219 — hover waits; focus does not.
 *
 * The bubble used to open on the FIRST mouseenter frame, so sweeping the pointer
 * across a toolbar of info glyphs fired a train of tooltips at everything it
 * crossed. 300ms is the intent threshold: long enough that a pass-through never
 * opens anything, short enough that a deliberate hover does not feel gated.
 * FOCUS stays instant — a keyboard user has already committed by tabbing there,
 * and WCAG 1.4.13/2.1.1 (the reason this component exists, see above) get no
 * value from making them wait.
 */
const OPEN_DELAY_MS = 300;

/**
 * The warm-up window, shared across every instance: once ONE tooltip has shown,
 * moving along a row of triggers should read each instantly — re-paying the
 * intent delay on every glyph makes a comparison scan (exactly what info-icon
 * rows are for) feel broken. Module-level on purpose; `0` means "cold".
 */
const WARM_MS = 500;
let lastClosedAt = 0;

/*
  § 327 — focus opens the bubble only when that focus could have come from the
  KEYBOARD. The rule is the browser's own `:focus-visible` heuristic: after a
  pointer press, focus that follows (a click, or a surface moving focus in
  programmatically) does not announce itself.

  Why not `el.matches(':focus-visible')` itself: jsdom answers `false` for every
  focus, so the whole suite could neither exercise nor keep this; tracking the
  last input modality is the same rule and behaves identically in both.

  The incident: a SidePanel moves focus to its first focusable element on open, and
  in the Guide's Settings drawer that is the title's InfoTooltip. Opened with a
  mouse click, the tip popped up by itself and stayed up — it closes on blur, and
  nothing blurred it. Keyboard users still get it instantly on Tab/Shift+Tab (and
  on a keyboard-opened drawer, where the announcement is wanted); hover is
  untouched. Starts as 'keyboard' so a focus with no prior input still opens.
*/
let lastInputWasPointer = false;
if (typeof document !== 'undefined') {
  const MODIFIERS = new Set(['Shift', 'Control', 'Alt', 'Meta']);
  document.addEventListener('keydown', (e) => {
    if (!MODIFIERS.has(e.key)) lastInputWasPointer = false;
  }, true);
  const onPointer = () => { lastInputWasPointer = true; };
  document.addEventListener('pointerdown', onPointer, true);
  document.addEventListener('mousedown', onPointer, true);
  document.addEventListener('touchstart', onPointer, true);
}

const anchoredPlacement: Record<TooltipPlacement, string> = {
  top: 'tw-bottom-full tw-left-1/2 tw--translate-x-1/2 tw-mb-1',
  bottom: 'tw-top-full tw-left-1/2 tw--translate-x-1/2 tw-mt-1',
  left: 'tw-right-full tw-top-1/2 tw--translate-y-1/2 tw-me-1',
  right: 'tw-left-full tw-top-1/2 tw--translate-y-1/2 tw-ms-1',
};

/*
 * Both maps take BOTH axes since § 309. `edgeY` used not to exist: the correction
 * effect measured `rect.left` / `rect.right` only, so a bubble could be nudged back
 * from a side edge but never from the top or bottom of the viewport. A `left`/`right`
 * placement centres its bubble on the trigger (`-50%`), so a tall one on a row near
 * either end of the screen hung off it with nothing pulling it back.
 */
const portalTransform: Record<TooltipPlacement, (edgeX: number, edgeY: number) => string> = {
  top: (x, y) => `translate(calc(-50% + ${x}px), calc(-100% + ${y}px))`,
  bottom: (x, y) => `translate(calc(-50% + ${x}px), ${y}px)`,
  /* `left`/`right` take the edge correction too. They used to ignore it, so a
     right-placed tip on a control near the right edge had nothing pulling it back. */
  left: (x, y) => `translate(calc(-100% + ${x}px), calc(-50% + ${y}px))`,
  right: (x, y) => `translate(${x}px, calc(-50% + ${y}px))`,
};

/** The anchored (non-portal) equivalent, for the same reason. */
const anchoredTransform: Record<TooltipPlacement, (edgeX: number, edgeY: number) => string> = {
  top: (x, y) => `translate(calc(-50% + ${x}px), ${y}px)`,
  bottom: (x, y) => `translate(calc(-50% + ${x}px), ${y}px)`,
  left: (x, y) => `translate(${x}px, calc(-50% + ${y}px))`,
  right: (x, y) => `translate(${x}px, calc(-50% + ${y}px))`,
};

/** § 343 — the side a bubble flips to when its own has no room. */
const OPPOSITE: Record<TooltipPlacement, TooltipPlacement> = {
  top: 'bottom',
  bottom: 'top',
  left: 'right',
  right: 'left',
};

/** The portal anchor point for a side of the trigger, 4px off it. */
const anchorPoint = (side: TooltipPlacement, rect: DOMRect): { top: number; left: number } => {
  switch (side) {
    case 'top': return { top: rect.top - 4, left: rect.left + rect.width / 2 };
    case 'bottom': return { top: rect.bottom + 4, left: rect.left + rect.width / 2 };
    case 'left': return { top: rect.top + rect.height / 2, left: rect.left - 4 };
    default: return { top: rect.top + rect.height / 2, left: rect.right + 4 };
  }
};

/** § 343 — the room a side of the trigger offers, inside the edge margin. */
const roomOn = (side: TooltipPlacement, rect: DOMRect): number => {
  switch (side) {
    case 'top': return rect.top - EDGE_MARGIN;
    case 'bottom': return window.innerHeight - rect.bottom - EDGE_MARGIN;
    case 'left': return rect.left - EDGE_MARGIN;
    default: return window.innerWidth - rect.right - EDGE_MARGIN;
  }
};

/** Whether a bubble on `side` runs past the viewport on that side's own axis. */
const overflowsItsSide = (side: TooltipPlacement, rect: DOMRect): boolean => {
  switch (side) {
    case 'top': return rect.top < EDGE_MARGIN;
    case 'bottom': return rect.bottom > window.innerHeight - EDGE_MARGIN;
    case 'left': return rect.left < EDGE_MARGIN;
    default: return rect.right > window.innerWidth - EDGE_MARGIN;
  }
};

const Tooltip = React.forwardRef<HTMLSpanElement, TooltipProps>(function Tooltip({
  content,
  children,
  placement = 'top',
  disabled = false,
  /* § 343 — portalled by DEFAULT, as `InfoTooltip` always was. Inline, the bubble is
     clipped by any `overflow` ancestor: the DS Table's own scroller, a Card, a drawer
     body. `usePortal={false}` is the inline escape hatch. */
  usePortal = true,
  block = false,
  shortcut,
  describeChild = true,
  className = '',
  'data-testid': dataTestId,
  ...rest
}, ref) {
  const [open, setOpen] = useState(false);
  const [anchor, setAnchor] = useState<{ top: number; left: number } | null>(null);
  const [edgeOffset, setEdgeOffset] = useState(0);
  /** § 309 — the vertical half of the same correction. */
  const [edgeOffsetY, setEdgeOffsetY] = useState(0);
  /* § 343 — the side the bubble is ACTUALLY on: `placement` until a flip. The ref is
     for callbacks (`measureAnchor` runs from scroll listeners), so they never read a
     side from a stale render. */
  const [side, setSide] = useState<TooltipPlacement>(placement);
  const sideRef = useRef<TooltipPlacement>(placement);
  /* One flip decision per open, so a bubble can never oscillate between sides. */
  const flipDecided = useRef(false);
  const triggerRef = useRef<HTMLSpanElement>(null);
  const bubbleRef = useRef<HTMLDivElement>(null);
  const closeTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const openTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const tipId = `${React.useId()}-tooltip`;

  const cancelClose = useCallback(() => {
    if (closeTimer.current) {
      clearTimeout(closeTimer.current);
      closeTimer.current = null;
    }
  }, []);

  const cancelOpen = useCallback(() => {
    if (openTimer.current) {
      clearTimeout(openTimer.current);
      openTimer.current = null;
    }
  }, []);

  /* Records the warm-up timestamp at every path that actually closes the bubble,
     so the next trigger in a row opens without re-paying the intent delay. */
  const settle = useCallback((wasOpen: boolean) => {
    if (wasOpen) lastClosedAt = Date.now();
  }, []);

  const close = useCallback(() => {
    cancelClose();
    cancelOpen();
    setOpen((was) => {
      settle(was);
      return false;
    });
    setEdgeOffset(0);
    setEdgeOffsetY(0);
  }, [cancelClose, cancelOpen, settle]);

  const scheduleClose = useCallback(() => {
    cancelClose();
    cancelOpen();
    closeTimer.current = setTimeout(
      () =>
        setOpen((was) => {
          settle(was);
          return false;
        }),
      CLOSE_DELAY_MS,
    );
  }, [cancelClose, cancelOpen, settle]);

  // A pending timer that fires after unmount sets state on a dead component.
  useEffect(
    () => () => {
      cancelClose();
      cancelOpen();
    },
    [cancelClose, cancelOpen],
  );

  /* § 242 — the portal anchor, measured at open AND re-measured on scroll/resize
     while the bubble is up (`useAnchorUpdates` below). A portalled tip used to keep
     the coordinates of its first frame, so scrolling a drawer under an open tooltip
     left the bubble hanging in space. */
  const measureAnchor = useCallback(() => {
    if (!triggerRef.current) return;
    setAnchor(anchorPoint(sideRef.current, triggerRef.current.getBoundingClientRect()));
  }, []);

  /* § 343 — every open starts from the caller's `placement` with no nudge. The § 309
     vertical offset used to survive a close (only X was reset), so a bubble clamped
     once opened nudged the next time too, wherever its trigger had moved. */
  const openNow = useCallback(() => {
    cancelClose();
    cancelOpen();
    setEdgeOffset(0);
    setEdgeOffsetY(0);
    sideRef.current = placement;
    setSide(placement);
    flipDecided.current = false;
    if (usePortal) measureAnchor();
    setOpen(true);
  }, [cancelClose, cancelOpen, measureAnchor, usePortal, placement]);

  useAnchorUpdates(open && usePortal, triggerRef, measureAnchor);

  /* § 343 — a dismissable LAYER, so Escape is answered from anywhere and by the
     topmost surface only. Escape used to be the trigger's own `onKeyDown`: a tooltip
     opened by hover, with focus elsewhere, could not be dismissed from the keyboard
     (WCAG 1.4.13), and inside a Dialog the same keypress closed the Dialog as well.
     Now the first Escape closes the tooltip and the next reaches the Dialog; with no
     tooltip open the Dialog answers at once (§ 23). Outside clicks are not a dismissal
     here — the bubble already closes on leave and blur. */
  useDismissable({
    isOpen: open,
    onDismiss: close,
    closeOnOutsidePointerDown: false,
  });

  /* The hover path (§ 219): wait out OPEN_DELAY_MS unless a tooltip closed within
     the warm-up window. Focus never comes through here — it calls openNow. */
  const scheduleOpen = useCallback(() => {
    cancelClose();
    if (Date.now() - lastClosedAt < WARM_MS) {
      openNow();
      return;
    }
    cancelOpen();
    openTimer.current = setTimeout(openNow, OPEN_DELAY_MS);
  }, [cancelClose, cancelOpen, openNow]);

  /**
   * Keep the bubble inside the viewport. One-shot: `edgeOffset` is deliberately
   * absent from the dependency list, so applying the correction does not re-measure
   * and oscillate.
   *
   * § 343 — FLIP first, clamp second. When the bubble runs past the viewport on its
   * own side (a `top` tip on a control near the top of the screen), it moves to the
   * opposite side if that side has more room; the next pass measures it there. The
   * § 309 clamp below only ever ran, and so slid a `top` bubble DOWN over the very
   * control it describes. It still runs after the flip decision, as the last resort
   * when neither side fits and for the cross axis. An unmeasured bubble (a zero box —
   * jsdom, or a frame before layout) is left alone, as `anchorIsClipped` does (§ 334).
   */
  useLayoutEffect(() => {
    if (!open || !bubbleRef.current) return;
    const rect = bubbleRef.current.getBoundingClientRect();
    if (rect.width === 0 && rect.height === 0) return;
    if (!flipDecided.current) {
      flipDecided.current = true;
      if (triggerRef.current && overflowsItsSide(side, rect)) {
        const trigger = triggerRef.current.getBoundingClientRect();
        const opposite = OPPOSITE[side];
        if (roomOn(opposite, trigger) > roomOn(side, trigger)) {
          sideRef.current = opposite;
          setSide(opposite);
          if (usePortal) setAnchor(anchorPoint(opposite, trigger));
          return;
        }
      }
    }
    let overflowX = 0;
    if (rect.left < EDGE_MARGIN) overflowX = rect.left - EDGE_MARGIN;
    else if (rect.right > window.innerWidth - EDGE_MARGIN) {
      overflowX = rect.right - (window.innerWidth - EDGE_MARGIN);
    }
    /*
     * § 309 — the vertical arm. Top wins over bottom deliberately: a bubble is capped
     * at 320px so it always fits between the margins, and pulling the top in is what
     * keeps the FIRST line readable. Without the cap this order would be wrong —
     * clamping the top of an over-tall bubble pushes its bottom further out.
     */
    let overflowY = 0;
    if (rect.top < EDGE_MARGIN) overflowY = rect.top - EDGE_MARGIN;
    else if (rect.bottom > window.innerHeight - EDGE_MARGIN) {
      overflowY = rect.bottom - (window.innerHeight - EDGE_MARGIN);
    }
    if (overflowX !== 0) setEdgeOffset((prev) => prev - overflowX);
    if (overflowY !== 0) setEdgeOffsetY((prev) => prev - overflowY);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, side, usePortal, anchor]);

  if (disabled) return <>{children}</>;

  /*
    `aria-describedby` is cloned onto the trigger, and only while the bubble is
    mounted — a reference to an id that is not in the document describes nothing.
    This is what `HoverTooltip` never did: its `role="tooltip"` bubble was
    unreachable by assistive technology no matter how it was triggered.
  */
  const describedChild = React.isValidElement(children)
    ? React.cloneElement(children as React.ReactElement<{ 'aria-describedby'?: string; 'aria-keyshortcuts'?: string }>, {
        /* § 388 — only a bubble that ADDS something describes the trigger; one that repeats
           the trigger's name (describeChild={false}) would be heard twice. */
        'aria-describedby':
          [(children.props as { 'aria-describedby'?: string })['aria-describedby'], open && describeChild ? tipId : undefined]
            .filter(Boolean)
            .join(' ') || undefined,
        /* § 303 — the shortcut is TOLD on the trigger (the § 247 one-source rule); a
           trigger that already declares one keeps its own. */
        ...(shortcut && !(children.props as { 'aria-keyshortcuts'?: string })['aria-keyshortcuts']
          ? { 'aria-keyshortcuts': shortcut }
          : {}),
      })
    : children;

  /*
    INVERSE since § 50 (2026-08-25). The bubble was `bg-white` + a `grey-200`
    hairline on a white page — separated from the content underneath by 1.39:1
    and a shadow. ADS tooltips are inverse (`color.background.neutral.bold`
    #292A2E under `color.text.inverse`, 14.34:1); ours inverts in the brand ink:
    `surface-inverse` under `ink-inverse`, 14.10:1. The inverse ground IS the
    separation, so the border and the shadow — the design system's one
    off-system shadow value — go with it. The 220px MIN-width goes too: it was
    invisible on a white bubble, but it would render a two-word tip as a dark
    plank. `max-w` still bounds long content.

    `w-max` is load-bearing and was missing (§ 163). Without it the bubble is a
    shrink-to-fit box, and shrink-to-fit resolves against the CONTAINING BLOCK —
    which, for the un-portalled bubble, is the `relative` wrapper around the
    trigger. On a 15px "ⓘ" that is 15px of available width, so a sentence
    rendered one word per line down a narrow column; `max-w` caps a width, it
    never grants one. The portalled bubble had the same failure against
    `viewport - left` near an edge. `width: max-content` sizes to the text and
    lets `max-w-[300px]` do the bounding it was always meant to do.

    § 301 (2026-09-15) — the bubble is a RAISED POPOVER in both themes: `tooltip-surface`
    under `tooltip-ink`, plus one chrome token, `ds-tooltip`, that folds the edge
    hairline (as a ring, so the box never grows) and the popover elevation into one
    utility. Judged by eye on live field-help bubbles, dark first and then light: in
    dark the inverse plank was the page's near-white ink, the loudest object on the
    page; in light it was a four-line slab in the primary action's navy, beside the
    Create button it out-shouted. The § 50 argument above was against a WEAK white
    bubble — a hairline and an off-system shadow, before the elevation ladder
    existed; this is the strong version, the chrome every Menu and Dropdown listbox
    already wears on the same pages. Field help here is a paragraph, not a two-word
    label, and a paragraph wants a card. The inverse pair keeps its other duties.
  */
  const bubble = (
    /* `tip-in` (§ 219), not the 200ms overlay fade: a tooltip is pointer-chrome,
       not an overlay — 120ms fade-and-settle from 97% reads as attached where the
       long fade read as the page loading something. The OUTER positioned element
       cannot carry it: its transform IS the anchor math, and a keyframe transform
       would override it mid-flight. Entrance-only, deliberately — an exit
       animation would hold the bubble over a pointer that already left. */
    <div
      /*
        § 309 — the bubble is bounded on BOTH axes now. `max-w-[300px]` has been here
        since § 50; there was no vertical counterpart at all, so a tooltip grew to the
        height of whatever it was given. The ISR promote dialog handed one 69 model
        names and produced a ~1,900px bubble on a ~775px viewport, centred on its
        trigger by the `right` placement's `translateY(-50%)` — so it ran off the top
        AND the bottom at once, with no way to read either end.

        **A scrollbar here is genuinely reachable, which is not true of tooltips in
        general.** This one is hover-persistent: the positioned wrapper below carries
        `onMouseEnter={cancelClose}`, so moving the pointer onto the bubble keeps it
        open. That is the property that makes `overflow-y-auto` a fix rather than a
        decoration.

        `overscroll-y-contain`, and only Y: the bubble can now scroll vertically, so
        stopping the wheel from chaining to the Dialog behind it is a promise it can
        keep. X is left alone — § 292's rule is never to contain an axis the element
        cannot actually scroll, and `max-w` + wrapping means there is no horizontal
        range to absorb.

        320px is a cap, not a target: a one-line tooltip is unaffected, and 320 is
        already ~40% of a short viewport. The cap is also what makes the vertical edge
        clamp below well-behaved — clamping the top of a bubble TALLER than the
        viewport would just push its bottom further out.
      */
      className="tw-px-2 tw-py-1 tw-bg-tooltip-surface tw-shadow-ds-tooltip tw-rounded-ctl tw-flex tw-items-start tw-w-max tw-max-w-[300px] tw-max-h-[20rem] tw-overflow-y-auto tw-overscroll-y-contain tw-font-noto-sans motion-safe:tw-animate-tip-in"
    >
      <span className="tw-min-w-0 tw-text-tooltip-ink tw-text-caption-metadata tw-break-words tw-text-left">
        {content}
      </span>
      {/* § 303 — the shortcut's picture (Menu's § 247 arrangement): caps after the words,
          aria-hidden because `aria-keyshortcuts` on the trigger is the fact. */}
      {shortcut && (
        <span
          aria-hidden="true"
          className="tw-ml-2 tw-inline-flex tw-shrink-0 tw-items-center tw-gap-1"
          data-testid={dataTestId ? `${dataTestId}-shortcut` : undefined}
        >
          {keysOf(shortcut).map((key, index) => (
            // eslint-disable-next-line react/no-array-index-key -- a chord can legitimately repeat a key
            <Kbd key={`${key}-${index}`}>{key}</Kbd>
          ))}
        </span>
      )}
    </div>
  );

  return (
    <span
      {...rest}
      ref={ref}
      className={withClassName(`tw-relative ${block ? 'tw-block tw-w-full' : 'tw-inline-block'}`, className)}
      data-testid={dataTestId}
    >
      {/* An inner span carries the measuring ref so the forwarded one stays the
          caller's to use — see `useMergedRef`'s docblock for the alternative. */}
      <span
        ref={triggerRef}
        className={block ? 'tw-block tw-w-full' : 'tw-inline-block'}
        /* Hover pays the intent delay (or rides the warm-up window); focus is
           instant — see the § 219 note on the constants. */
        onMouseEnter={scheduleOpen}
        onMouseLeave={scheduleClose}
        onFocus={() => { if (!lastInputWasPointer) openNow(); }}
        onBlur={close}
        /* § 343 — no `onKeyDown` Escape here any more: `useDismissable` answers it at the
           document, for hover-opened tips too. A handler here would close the tooltip
           BEFORE the document listeners run — and a real browser flushes the resulting
           effects between listeners, so the tooltip's layer would already be gone when
           the Dialog's listener asked who is topmost, and the Dialog would close too. */
      >
        {describedChild}
      </span>

      {open && !usePortal && (
        <div
          ref={bubbleRef}
          id={tipId}
          role="tooltip"
          className={`tw-absolute tw-z-overlay ${anchoredPlacement[side]}`}
          style={edgeOffset !== 0 || edgeOffsetY !== 0
            ? { transform: anchoredTransform[side](edgeOffset, edgeOffsetY) }
            : undefined}
          onMouseEnter={cancelClose}
          onMouseLeave={scheduleClose}
          data-testid={dataTestId ? `${dataTestId}-bubble` : undefined}
        >
          {bubble}
        </div>
      )}

      {open && usePortal && anchor && createPortal(
        <div
          ref={bubbleRef}
          id={tipId}
          role="tooltip"
          className="tw-fixed tw-z-tooltip"
          style={{
            top: `${anchor.top}px`,
            left: `${anchor.left}px`,
            transform: portalTransform[side](edgeOffset, edgeOffsetY),
          }}
          onMouseEnter={cancelClose}
          onMouseLeave={scheduleClose}
          data-testid={dataTestId ? `${dataTestId}-bubble` : undefined}
        >
          {bubble}
        </div>,
        document.body,
      )}
    </span>
  );
});

export default Tooltip;
